import { useEffect, useRef } from 'react';
import { ACTION_TYPES, useSubmissionSendDispatch, useSubmissionSendState } from './submission-send-context';
import SubmissionAppService from '../../../../application/platform/ser/submission/submission-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { SUBMISSION_LIMITS } from '../../../../model/ser/ser-enums';
import { mapSubmissionErrors } from '../submission-error-utils';
import {
    SendStep, SubmissionDraft, addFiles, defaultVehicleIds, expiredDefaultVehicles, firstInvalidStep, isDraftEmpty, isStepValid,
    isVehicleExpired, removeExpiredVehicles, toSubmitItem, withDefaultVehicles,
} from '../submission-send-rules';
import { formatDate } from '../../../../utils/date';

export const useSubmissionSendController = (clientId?: number) => {
    const dispatch = useSubmissionSendDispatch();
    const state = useSubmissionSendState();
    const { showError, showWarning, confirmAction } = useFeedback();
    // Trava síncrona contra duplo clique (o estado "sending" só muda no próximo render)
    const sendingRef = useRef(false);

    const activeDraft = state.drafts.find((d) => d.key === state.activeKey) ?? state.drafts[0];
    const hasUnsent = !state.result && state.drafts.some((d) => !isDraftEmpty(d));

    useEffect(() => {
        if (!clientId) return;
        dispatch({ type: ACTION_TYPES.setVehiclesLoading, payload: true });
        SubmissionAppService.listVehicles(clientId)
            .then((vehicles) => dispatch({ type: ACTION_TYPES.setVehicles, payload: vehicles.filter((v) => v.isActive) }))
            .catch((error) => {
                dispatch({ type: ACTION_TYPES.setVehicles, payload: [] });
                showError('Não foi possível carregar os veículos de publicação', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    // Aviso do navegador ao fechar/recarregar com remessas não enviadas
    useEffect(() => {
        if (!hasUnsent) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsent]);

    // Ao trocar a data, remove os veículos cujo prazo já encerrou para a nova data
    function updateDraft(patch: Partial<SubmissionDraft>) {
        if ('publishDate' in patch) {
            const { vehicleIds, removed } = removeExpiredVehicles({ ...activeDraft, ...patch }, state.vehicles);
            if (removed.length) {
                patch = { ...patch, vehicleIds };
                showWarning(`Prazo de envio encerrado para a nova data: ${removed.join(', ')} ${removed.length === 1 ? 'foi removido' : 'foram removidos'} da remessa.`);
            }
            const expiredDefaults = expiredDefaultVehicles({ ...activeDraft, ...patch }, state.vehicles);
            if (expiredDefaults.length) {
                showWarning(`O prazo de envio do ${expiredDefaults.map((v) => v.name).join(', ')} já encerrou para esta data. Escolha outra data de publicação.`);
            }
        }
        dispatch({ type: ACTION_TYPES.updateDraft, payload: patch });
    }

    // Pelo stepper: etapas já alcançadas ou a próxima quando as anteriores estão válidas
    function goToStep(step: SendStep) {
        const reached = state.reached[state.activeKey] ?? SendStep.Data;
        const previousValid = [SendStep.Data, SendStep.Files, SendStep.Vehicles].filter((s) => s < step).every((s) => isStepValid(activeDraft, s));
        if (step <= reached || previousValid) {
            dispatch({ type: ACTION_TYPES.setStep, payload: step });
        } else {
            dispatch({ type: ACTION_TYPES.setShowErrors, payload: true });
        }
    }

    function next() {
        if (!isStepValid(activeDraft, state.activeStep)) {
            dispatch({ type: ACTION_TYPES.setShowErrors, payload: true });
            return;
        }
        dispatch({ type: ACTION_TYPES.setStep, payload: Math.min(state.activeStep + 1, SendStep.Review) });
    }

    function back() {
        dispatch({ type: ACTION_TYPES.setStep, payload: Math.max(state.activeStep - 1, SendStep.Data) });
    }

    function handleAddFiles(files: File[]) {
        const result = addFiles(activeDraft.files, files);
        updateDraft({ files: result.files });
        dispatch({ type: ACTION_TYPES.setRejectedFiles, payload: result.rejected });
    }

    function handleRemoveFile(index: number) {
        updateDraft({ files: activeDraft.files.filter((_, i) => i !== index) });
    }

    function toggleVehicle(id: number) {
        const selected = activeDraft.vehicleIds.includes(id);
        const vehicle = state.vehicles.find((v) => v.id === id);
        if (vehicle?.isDefault) return;
        if (!selected && vehicle && isVehicleExpired(vehicle, activeDraft.publishDate)) return;
        updateDraft({ vehicleIds: selected ? activeDraft.vehicleIds.filter((v) => v !== id) : [...activeDraft.vehicleIds, id] });
    }

    function selectAllVehicles() {
        const available = state.vehicles.filter((v) => !isVehicleExpired(v, activeDraft.publishDate)).map((v) => v.id);
        const allSelected = available.length > 0 && available.every((id) => activeDraft.vehicleIds.includes(id));
        updateDraft({ vehicleIds: allSelected ? defaultVehicleIds(state.vehicles) : withDefaultVehicles(available, state.vehicles) });
    }

    function addDraft() {
        if (state.drafts.length >= SUBMISSION_LIMITS.maxSubmissionsPerBatch) {
            showWarning(`O envio aceita até ${SUBMISSION_LIMITS.maxSubmissionsPerBatch} remessas. Envie estas e continue em um novo envio.`);
            return;
        }
        dispatch({ type: ACTION_TYPES.addDraft });
    }

    function duplicateDraft(key: string) {
        if (state.drafts.length >= SUBMISSION_LIMITS.maxSubmissionsPerBatch) {
            showWarning(`O envio aceita até ${SUBMISSION_LIMITS.maxSubmissionsPerBatch} remessas.`);
            return;
        }
        dispatch({ type: ACTION_TYPES.duplicateDraft, payload: key });
    }

    function removeDraft(draft: SubmissionDraft) {
        const remove = () => dispatch({ type: ACTION_TYPES.removeDraft, payload: draft.key });
        if (isDraftEmpty(draft)) remove();
        else confirmAction('Remover remessa', `Remover "${draft.title.trim() || 'Remessa sem título'}" deste envio?`, remove);
    }

    function selectDraft(key: string) {
        dispatch({ type: ACTION_TYPES.selectDraft, payload: key });
    }

    async function send() {
        if (sendingRef.current || !clientId) return;

        // O diário próprio não sai da remessa: com o prazo dele encerrado, a data de publicação precisa mudar
        const blockedDraft = state.drafts.find((d) => expiredDefaultVehicles(d, state.vehicles).length > 0);
        if (blockedDraft) {
            const names = expiredDefaultVehicles(blockedDraft, state.vehicles).map((v) => v.name).join(', ');
            dispatch({ type: ACTION_TYPES.selectDraft, payload: blockedDraft.key });
            dispatch({ type: ACTION_TYPES.setStep, payload: SendStep.Data });
            showWarning(`O prazo de envio do ${names} encerrou para ${formatDate(blockedDraft.publishDate)}. Escolha outra data de publicação.`);
            return;
        }

        // O prazo pode ter encerrado com a tela aberta: retira os veículos vencidos antes de enviar
        const expired = state.drafts
            .map((draft) => ({ draft, ...removeExpiredVehicles(draft, state.vehicles) }))
            .filter((item) => item.removed.length > 0);
        if (expired.length) {
            expired.forEach(({ draft, vehicleIds }) => dispatch({ type: ACTION_TYPES.setDraftVehicles, payload: { key: draft.key, vehicleIds } }));
            dispatch({ type: ACTION_TYPES.selectDraft, payload: expired[0].draft.key });
            dispatch({ type: ACTION_TYPES.setStep, payload: SendStep.Vehicles });
            showWarning(`O prazo de envio encerrou para ${Array.from(new Set(expired.flatMap((item) => item.removed))).join(', ')}. Os veículos foram removidos; revise a remessa antes de enviar.`);
            return;
        }
        const pending = state.drafts.find((d) => firstInvalidStep(d) !== SendStep.Review);
        if (pending) {
            dispatch({ type: ACTION_TYPES.selectDraft, payload: pending.key });
            dispatch({ type: ACTION_TYPES.setShowErrors, payload: true });
            showWarning(state.drafts.length > 1 ? 'Há remessas pendentes. Complete-as antes de enviar.' : 'Complete os dados da remessa antes de enviar.');
            return;
        }

        sendingRef.current = true;
        dispatch({ type: ACTION_TYPES.sendStart });
        const onProgress = (percent: number) => dispatch({ type: ACTION_TYPES.setProgress, payload: percent });
        try {
            const items = state.drafts.map(toSubmitItem);
            const response = items.length === 1
                ? await SubmissionAppService.submit(items[0], clientId, onProgress)
                : await SubmissionAppService.submitBatch(items, clientId, onProgress);
            dispatch({ type: ACTION_TYPES.sendSuccess, payload: { submissionIds: response.submissionIds ?? [], batchId: response.batchId ?? undefined } });
        } catch (error: any) {
            const errors = mapSubmissionErrors(error);
            dispatch({ type: ACTION_TYPES.sendError, payload: errors });
            showError(errors.message, { ...error, message: errors.message });
        } finally {
            sendingRef.current = false;
        }
    }

    function discard() {
        if (!hasUnsent) {
            dispatch({ type: ACTION_TYPES.reset });
            return;
        }
        confirmAction('Descartar remessas', 'Descartar as remessas não enviadas?', () => dispatch({ type: ACTION_TYPES.reset }));
    }

    function startNew() {
        dispatch({ type: ACTION_TYPES.reset });
    }

    return {
        activeDraft, hasUnsent,
        updateDraft, goToStep, next, back,
        handleAddFiles, handleRemoveFile, toggleVehicle, selectAllVehicles,
        addDraft, duplicateDraft, removeDraft, selectDraft,
        send, discard, startNew,
    };
};
